import { GSplatPickIndex } from '@/plugins/gsplat/unified/GSplatPickIndex'
import { getLoggerManager } from '@/shared/logging'
import type { GSplatInfo } from './GSplatInfo'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 版本化的世界状态快照
 *
 * 核心功能：
 * 1. 维护一个特定版本的所有 GSplat 快照
 * 2. 估算和分配排序纹理的布局（每个 splat 分配行号）
 * 3. 管理延迟释放的资源引用
 * 4. 跟踪排序状态
 *
 * @remarks
 * 对应 PlayCanvas 的 GSplatWorldState 类
 * 关键差异：
 * - 移除了 device 依赖，简化纹理大小估算
 * - assignLines() 返回总使用像素数
 */
export class GSplatWorldState {
  /** 与本 WorldState/WorkBuffer layout 同代的不可变 original identity 快照。 */
  readonly pickIndex: GSplatPickIndex
  /**
   * 世界状态版本号
   * 每次场景布局变化时递增
   */
  version: number = 0

  /**
   * 是否已经在排序器上设置过参数
   */
  sortParametersSet: boolean = false

  /**
   * 是否已排序过至少一次
   * 首次排序后设置为 true
   */
  sortedBefore: boolean = false

  /**
   * 本状态管理的所有 splat 快照
   */
  splats: GSplatInfo[] = []

  /**
   * 工作缓冲区的纹理大小（宽度 = 高度）
   * 所有 splat 的数据会被打包到这个正方形纹理中
   */
  textureSize: number = 0

  /**
   * 实际使用的像素数量
   * 等于 (使用的行数 × textureSize)
   *
   * @remarks
   * 这个值应该传递给排序 worker 和渲染器，而非纹理的总像素数
   */
  totalUsedPixels: number = 0

  /** active splat 数量，不包含行 padding */
  totalActiveSplats: number = 0

  /** 排序 worker compact 后的可见 splat 数量，不包含行 padding */
  sortedVisibleSplatCount: number = 0

  /** 创建该布局时对应的 manager 裁切粗裁版本。 */
  clipLodVersion: number = 0

  constructor(
    version: number,
    splats: GSplatInfo[],
    maxTextureSize: number = 8192,
    minTextureSize: number = 1,
    clipLodVersion: number = 0,
    pickIndex: GSplatPickIndex = GSplatPickIndex.empty()
  ) {
    this.version = version
    this.splats = splats
    this.clipLodVersion = clipLodVersion
    this.pickIndex = pickIndex

    // 估算所需的纹理大小
    this.estimateTextureSize(splats, maxTextureSize, minTextureSize)

    // 为每个 splat 分配纹理行
    this.assignLines(splats, this.textureSize)
  }

  /**
   * 使用二分查找估算能容纳所有 splat 的最小正方形纹理尺寸
   *
   * @param splats - 要分配空间的 splat 数组
   * @param maxSize - 最大纹理尺寸（宽度和高度）
   * @param minSize - 最小纹理尺寸；用于对齐 PlayCanvas splatBudget 预分配容量
   * @returns 是否找到有效的纹理尺寸
   *
   * @remarks
   * 算法逻辑：
   * 1. 对于每个尺寸，计算每个 splat 需要的行数：Math.ceil(activeSplats / size)
   * 2. 累加所有 splat 的行数，检查是否超过尺寸限制
   * 3. 使用二分查找找到最小满足条件的尺寸
   */
  private estimateTextureSize(splats: GSplatInfo[], maxSize: number, minSize: number = 1): boolean {
    /**
     * 检查给定尺寸是否能容纳所有 splat
     */
    const fits = (size: number): boolean => {
      let rows = 0
      for (const splat of splats) {
        rows += Math.ceil(splat.activeSplats / size)
        if (rows > size) return false
      }
      return true
    }

    const safeMinSize = Math.max(1, Math.min(maxSize, Math.floor(minSize)))
    let low = safeMinSize
    let high = maxSize
    let bestSize: number | null = null

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      if (fits(mid)) {
        bestSize = mid
        high = mid - 1 // 尝试更小的尺寸
      } else {
        low = mid + 1
      }
    }

    if (bestSize === null) {
      this.textureSize = 0
      log.error('GSplatWorldState.estimateTextureSize: failed to find a valid texture size')
      return false
    }

    this.textureSize = bestSize
    return true
  }

  /**
   * 为每个 splat 分配纹理行
   *
   * @param splats - 要分配行的 splat 数组
   * @param size - 纹理尺寸
   *
   * @remarks
   * 分配策略：
   * 1. 每个 splat 占用连续的行
   * 2. 每行最多容纳 size 个 splat
   * 3. 计算每个 splat 需要的行数：Math.ceil(activeSplats / size)
   * 4. 更新 splat 的 lineStart, lineCount, padding 等字段
   */
  private assignLines(splats: GSplatInfo[], size: number): void {
    if (splats.length === 0) {
      this.totalUsedPixels = 0
      this.totalActiveSplats = 0
      this.sortedVisibleSplatCount = 0
      return
    }

    let start = 0
    this.totalActiveSplats = 0
    for (const splat of splats) {
      const activeSplats = splat.activeSplats
      this.totalActiveSplats += activeSplats
      const numLines = Math.ceil(activeSplats / size)
      splat.setLines(start, numLines, size, activeSplats)
      start += numLines
    }

    // 计算总使用的像素数 = 使用的行数 × 纹理宽度
    this.totalUsedPixels = start * size
    this.sortedVisibleSplatCount = this.totalActiveSplats
  }

  /**
   * 销毁世界状态，清理所有 splat
   */
  destroy(): void {
    this.splats.forEach((splat) => splat.destroy())
    this.splats.length = 0
  }
}
